import axios from "axios";
import router from "@/router/router";
import store from "@/store";
import { err, ok, Result } from "neverthrow";
import { checkExistInquiry } from "@/modules/staff/services/inquiry";
import { getBrowserLanguage } from "@/framework/utilities/getBrowserLanguage";
import { i18n } from "@/framework/plugins/i18n";
import { Trans } from "@/framework/plugins/translation";
import { DEFAULT_LANG_CD } from "@/framework/constants/trans";
import { AuthMutation } from "@/framework/store/auth/types";
import { ConversationMutation } from "@/framework/store/conversation/types";
import { UserProfile } from "@/modules/auth/objects/userProfile";
import { UserService } from "@/modules/auth/services/UserService";
import ErrorLogService, { IErrorLogService } from "@/framework/logger/errorLog/errorLogService";
import { ReturnType } from "@/framework/types/ReturnType";
import { CommonVuexStateRepository } from "@/framework/repositories/CommonVuexStateRepository";
import KtznAuthenticationError from "@/modules/auth/errors/ktznAuthenticationError";

/**
 * Error response from the Kotozna Auth server
 */
export type KtznAuthErrorResponse = {
  is_success: boolean;
  message: string;
  error_code?: string;
  error_id?: string;
};

/**
 * Success response from the Kotozna Auth server,
 * when calling `/login/request`
 */

export type KtznRequestLoginResponse = {
  has_sandbox: boolean;
  is_sandbox: boolean;
  is_session_active: boolean;
  message: string;
  success: boolean;
  transaction_code: string;
};

/**
 * Success response from the Kotozna Auth server,
 * when calling `/login/accept`
 */
export type KtznAcceptLoginResponse = {
  access_id: string;
  access_token_v2: string;
  id: string;
  refresh_token_v2: string;
  session_id: string;
  success: boolean;
  message: string;
  onetime_token: string;
  subscription_endpoint: string;
};

/**
 * Success response from the Kotozna Auth server,
 * when calling `/user/auth/refresh`
 */
export type KtznAuthRefreshResponse = {
  access_token: string;
  refresh_token: string;
  message: string;
  success: boolean;
};

/**
 * Authentication service interface for Kotozna Auth.
 */
export interface IKtznAuthenticationService {
  /**
   * Requesting a login to the Kotozna Auth server.
   *
   * - Calls the REST API `/login/request`
   *
   * @param loginId User email
   * @param isSandBox If requesting to sandbox? (Default: `false`)
   * @param target ???
   * @returns Either a `KtznRequestLoginResponse` or a `KtznAuthenticationError`
   */
  loginRequest(
    loginId: string,
    isSandBox: boolean,
    target: string
  ): Promise<Result<KtznRequestLoginResponse, KtznAuthenticationError>>;

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the PIN that the user received via email
   * - Calls the REST API `/login/accept`
   *
   * @param transactionCode Transaction code from the previous response
   * @param confirmationCode Confirmation code from that the user received via email
   * @returns Either a `KtznAcceptLoginResponse` or a `KtznAuthenticationError`
   */
  loginAccept(
    transactionCode: string,
    confirmationCode: string
  ): Promise<Result<KtznAcceptLoginResponse, KtznAuthenticationError>>;

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the onetime token that the user received via loginAccept API
   * - Calls the REST API `/login/accept_onetime`
   *
   * @param token Onetime token from the previous response
   * @param isSandbox true if you sign in as a sandbox user
   * @returns Either a `KtznAcceptLoginResponse` or a `KtznAuthenticationError`
   */

  loginAcceptOnetimeToken(
    token: string,
    isSandbox: boolean
  ): Promise<Result<KtznAcceptLoginResponse, KtznAuthenticationError>>;

  /**
   * Requests a new authentication token from the Kotozna Auth service.
   * @param refreshToken A valid refresh token
   * @returns Either a `KtznAuthRefreshResponse` or a `KtznAuthenticationError`
   */
  authRefresh(refreshToken: string): Promise<Result<KtznAuthRefreshResponse, KtznAuthenticationError>>;
}

/**
 * Authentication service for Kotozna Auth.
 */
export class KtznAuthenticationService implements IKtznAuthenticationService {
  private _ktznBeEndpoint: string;
  private vuexState: CommonVuexStateRepository;
  private errorLogService: IErrorLogService;

  constructor(
    ktznBeEndpoint: string = `${process.env.VUE_APP_KTZN_BE_ENDPOINT}`,
    vuexStateRepository = new CommonVuexStateRepository(),
    errorLogService = ErrorLogService
  ) {
    this._ktznBeEndpoint = ktznBeEndpoint;
    this.vuexState = vuexStateRepository;
    this.errorLogService = errorLogService;
  }

  /**
   * Requesting a login to the Kotozna Auth server.
   *
   * - Entering the email
   * - Calls the REST API `/login/request`
   *
   * @param loginId User email
   * @param isSandBox If requesting to sandbox? (Default: `false`)
   * @param target ???
   * @returns Either a `KtznRequestLoginResponse` or a `KtznAuthenticationError`
   */
  loginRequest = async (
    loginId: string,
    isSandBox: boolean = false,
    target: string = ""
  ): Promise<Result<KtznRequestLoginResponse, KtznAuthenticationError>> => {
    const url = `${this._ktznBeEndpoint}/login/request`;
    const requestObject = {
      login_id: loginId,
      is_sandbox: isSandBox,
      target: target,
    };
    const errorType = KtznAuthenticationError.Type.loginRequestFailed;
    return await this.postRequest<KtznRequestLoginResponse>(url, requestObject, errorType);
  };

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the PIN that the user received via email
   * - Calls the REST API `/login/accept`
   *
   * @param transactionCode Transaction code from the previous response
   * @param confirmationCode Confirmation code from that the user received via email
   * @returns Either a `KtznAcceptLoginResponse` or a `KtznAuthenticationError`
   */
  async loginAccept(
    transactionCode: string,
    confirmationCode: string
  ): Promise<Result<KtznAcceptLoginResponse, KtznAuthenticationError>> {
    const url = `${this._ktznBeEndpoint}/login/accept`;
    const requestObject = {
      transaction_code: transactionCode,
      confirmation_code: confirmationCode,
    };
    const errorType = KtznAuthenticationError.Type.loginAcceptFailed;
    return await this.postRequest<KtznAcceptLoginResponse>(url, requestObject, errorType);
  }

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the onetime token that the user received via loginAccept API
   * - Calls the REST API `/login/accept_onetime`
   *
   * @param token Onetime token from the previous response
   * @param isSandbox true if you sign in as a sandbox user
   * @returns Either a `KtznAcceptLoginResponse` or a `KtznAuthenticationError`
   */
  async loginAcceptOnetimeToken(
    token: string,
    isSandbox: boolean
  ): Promise<Result<KtznAcceptLoginResponse, KtznAuthenticationError>> {
    const url = `${this._ktznBeEndpoint}/login/accept_onetime`;
    const requestObject = {
      is_sandbox: isSandbox,
      token: token,
    };
    const errorType = KtznAuthenticationError.Type.loginAcceptFailed;
    return await this.postRequest<KtznAcceptLoginResponse>(url, requestObject, errorType);
  }

  /**
   * Requests a new authentication token from the Kotozna Auth service.
   * @param refreshToken A valid refresh token
   * @returns Either a `KtznAuthRefreshResponse` or a `KtznAuthenticationError`
   */
  async authRefresh(refreshToken: string): Promise<Result<KtznAuthRefreshResponse, KtznAuthenticationError>> {
    const url = `${this._ktznBeEndpoint}/login/token/refresh`;
    const requestObject = {
      refresh_token: refreshToken,
    };
    const errorType = KtznAuthenticationError.Type.authRefreshFailed;
    return await this.postRequest<KtznAuthRefreshResponse>(url, requestObject, errorType);
  }

  /**
   *
   * @param forceBrowserLanguage
   */
  initializeUser = async (forceBrowserLanguage = false) => {
    console.log("***** Initialize User *****");
    const user = await new UserService().getCachedQcUser();
    if (user.isErr()) {
      console.error("Failed to get user info");
      router.push(`/${i18n.locale}/initial`);
      return;
    }

    await this.changeUserLanguage(forceBrowserLanguage, user.value);
    if (user.value !== null && user.value.userType === "biz_staff" && user.value.sandboxType !== "sandbox") {
      // If staff member then check for inquiry
      await checkExistInquiry(user.value.langCd);
    }

    return user.value;
  };

  changeUserLanguage = async (forceBrowserLanguage: boolean, user: UserProfile | null) => {
    if (forceBrowserLanguage) {
      await new UserService().changeLanguage(getBrowserLanguage());
    } else {
      if (user !== null) {
        if (!user.langCd) {
          await new UserService().changeLanguage(i18n.locale);
        } else {
          if (i18n.locale != user.langCd) {
            try {
              await Trans.changeLanguage(user.langCd);
              Trans.currentLanguage = user.langCd;
              i18n.locale = user.langCd;
            } catch (e) {
              console.log("***** FE Language Update Error *****");
              await Trans.changeLanguage(DEFAULT_LANG_CD);
              window.location.reload();
            }
          }
        }
      }
    }
  };

  signInByToken = async (token: string) => {
    const loginToken = token.slice(0, token.length / 2);
    console.log("Attempting login via one-time token: ", loginToken + "***************");

    try {
      await store.dispatch("logout");
      store.commit(ConversationMutation.SET_HAS_STARTED_APPSYNC_SUBSCRIPTION, false);
      localStorage.clear();

      const url = `${process.env.VUE_APP_KTZN_BE_ENDPOINT}/login/token/validate`;
      const res = await axios.post(url, { token: token });

      // Graphql Ariadne token
      if (res.data && res.data.access_token_v2) {
        const accessTokenV2 = res.data.access_token_v2;
        store.commit(AuthMutation.SET_ACCESS_TOKEN, accessTokenV2);
      }

      store.commit(AuthMutation.SET_LAST_LOGIN_DATETIME, new Date().toString());
      return res.data.device_id;
    } catch (e) {
      await this.errorLogService.sendAuthLog("Auth.signIn", "signInByOneTimeToken", { loginToken }, JSON.stringify(e));
      console.error(`Error occurred in signInByOneTimeToken: ${JSON.stringify(e)}`);
      return ReturnType.FAIL;
    }
  };

  /**
   * Helper function to make a post request via axios
   * @param url Kotozna endpoint and path
   * @param requestObject Request data required to call this endpoint
   * @param errorType Errortype for this function
   * @returns A `Result` with either a `SuccessResponse` or `KtznAuthenticationError`
   */
  private postRequest = async <SuccessResponse>(
    url: string,
    requestObject: any,
    errorType: KtznAuthenticationError.Type
  ): Promise<Result<SuccessResponse, KtznAuthenticationError>> => {
    try {
      const res = await axios.post(url, requestObject);
      // Additional check because backend sends 200 and success `true` even if refreshing the token fails
      let message: string = res && res.data && res.data.message ? res.data.message : "";
      let isErrorMessage = message.startsWith("error:");
      if ((res.status == 200 || res.status == 201 || res.status == 202) && !isErrorMessage) {
        return ok(res.data as SuccessResponse);
      } else {
        const errorData: KtznAuthErrorResponse = res.data;
        return err(new KtznAuthenticationError(errorType, errorData));
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Axios error with access to config, request, and response
        const errorData: KtznAuthErrorResponse =
          error && error.response && error.response.data ? error.response.data : {};
        return err(new KtznAuthenticationError(errorType, errorData, error));
      } else {
        // A generic error
        return err(new KtznAuthenticationError(KtznAuthenticationError.Type.general, undefined, error));
      }
    }
  };
}
